/**
 * Calendar-date primitives — local dates, never instants.
 *
 * Every date these serve is a CALENDAR DATE: the day a client trains, the day a window opens. The
 * wire carries "YYYY-MM-DD", the backend parses it with `Kind=Unspecified`, takes `.Date` and stamps
 * it UTC, so the string we send is the exact date it stores. Nothing here may go through
 * `toISOString()`, which converts a local midnight to the previous day in every zone east of UTC —
 * Poland included, every day of the year.
 *
 * ── Promoted out of `features/assign-plan/model` on its second consumer ──────────────────────
 * Which is the trigger its own header named. `pages/client-detail` writes the same "YYYY-MM-DD"
 * from a live `Date` twice — the trends range's end and the attendance window's — and BOTH had
 * grown their own formatter, one of them through `toISOString()`. That is the near-duplicate the
 * house rule exists to stop, and the copy that was wrong is what the promotion is paying for.
 *
 * ── `occurrenceClock` — the hour an occurrence reads at, on THREE screens ────────────────────
 * It replaced `readWallClockTime` on 8 September 2026 and its doctrine with it. See that function
 * for what an occurrence's `moment` is; what belongs here is why the reader moved: the clock is a
 * question about the wire's shape, and three call sites asked it (the client calendar, the plan
 * grid's card, the session panel's head), so it lives once.
 *
 * ── Ported from `move-with-me-frontend/.../shared/lib/calendarDate.ts` ───────────────────────
 * `isoDayOfWeek` and `toIsoDate` are byte-equivalent to mobile's, because the schedule maths on
 * both sides of the wire is pinned by one fixture and a different weekday convention would move
 * every generated date by a day. The rest of mobile's module (month paging, the 12-month horizon)
 * is NOT ported: the horizon there bounds a calendar FETCH window this app does not make, and the
 * backend's own start-date ceiling is 730 days, so copying a 12-month clamp would refuse dates the
 * server accepts.
 *
 * No React, no DOM, no query hook, no i18next.
 */

/**
 * ISO weekday, 1 (Monday) … 7 (Sunday).
 *
 * `Date.getDay()` is 0 (Sunday) … 6, and the backend's `PlanDayTemplate.DayOfWeek` is ISO. Getting
 * this wrong shifts Sunday by six days and nothing else, which is why it is a named function rather
 * than an inline `?:` at three call sites.
 */
export function isoDayOfWeek(date: Date): number {
  const js = date.getDay();
  return js === 0 ? 7 : js;
}

/**
 * Monday-first weekday, 0 (Monday) … 6 (Sunday), of a "YYYY-MM-DD".
 *
 * The counterpart of `isoDayOfWeek` for the other kind of input: that one takes a `Date` and answers
 * the backend's 1…7 convention, this one takes the wire's bare date string and answers the index a
 * seven-column grid puts it in.
 *
 * Read in UTC on purpose, and this is the load-bearing half: a calendar date's weekday is not a zone
 * question, and `new Date("2026-08-03T00:00:00Z")` IS the UTC-midnight parse of exactly that date —
 * so unlike an instant-based read this cannot slide a mark onto the neighbouring day for a trainer
 * west of Greenwich.
 *
 * Promoted here on 2 September 2026 from its two byte-identical copies in
 * `pages/client-roster/model/clientPreview` and `pages/client-detail/model/attendance`. They existed
 * to place a mark in `shared/ui/TrainingWeekStrip` — the SAME component, from two sides — so the
 * two could have disagreed about which column a Sunday belongs to while both looked right in their
 * own test.
 */
export function mondayFirstWeekday(date: string): number {
  return (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7;
}

/** Local "YYYY-MM-DD". Never `toISOString()` — see the header. */
export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * A "YYYY-MM-DD" string as a LOCAL midnight `Date`, or `null` when it is not one.
 *
 * `new Date("2026-08-03")` parses as UTC midnight and renders as 2 August in every western zone,
 * so the whole review calendar would be a day early for half the world. The parts are read out and
 * handed to the local-time constructor instead.
 *
 * Round-trip checked rather than trusted: the constructor happily rolls `2026-02-31` over to
 * 3 March, and a date input can hand us one through a paste.
 */
export function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return toIsoDate(parsed) === value ? parsed : null;
}

/**
 * The occurrence's own "when", as the wire carries it (ADR 2026-09-08).
 *
 * Structural and minimal — assignable FROM the generated `MomentResponse`, never a hand-rolled copy
 * of it. `kind` is `string | null` on the wire and is read as such: a kind this build has never
 * heard of must fall back, not throw.
 */
export interface OccurrenceMoment {
  /** `Day` | `Booked` | `Lived`. */
  readonly kind?: string | null;
  /** The civil date the occurrence names, `YYYY-MM-DD`. A date, not an instant — read verbatim. */
  readonly date: string;
  /** The chosen wall time, `HH:mm:ss`. Null for `Day` AND for `Lived` — absence is not "no hour". */
  readonly time?: string | null;
  /** The IANA zone the day was cut in / the hour was chosen in. */
  readonly zoneId?: string | null;
  /** The derived instant. A true moment for `Booked`/`Lived`; local midnight, never shown, for `Day`. */
  readonly startUtc: string;
  /** The derived end instant, where one exists. */
  readonly endUtc?: string | null;
}

/**
 * What {@link occurrenceClock} needs off a response. Both field names are the wire's own, on all
 * three responses that carry an occurrence (`CalendarEventResponse`, `ExecutionSummarySlot`,
 * `WorkoutExecutionDisplayResponse`), so no caller renames anything on the way in.
 */
export interface OccurrenceTime {
  /**
   * The occurrence's moment. Optional because orval emits a nullable object that way, and because a
   * response from a backend older than the 2026-09-08 change carries none — not because it is a
   * state the current backend produces.
   */
  readonly moment?: OccurrenceMoment | null;
  /** The legacy true instant. Read only on the fallback arm below. */
  readonly eventInstant?: string | null;
}

/** A kind that names no hour at all. Its `startUtc` is local midnight and must never be printed. */
const DAY_KIND = 'Day';
/** The two kinds whose `startUtc` IS the hour: an hour somebody chose, and one somebody lived. */
const KINDS_WITH_AN_HOUR: readonly string[] = ['Booked', 'Lived'];

/**
 * The clock this occurrence reads at where the reader stands — `H:MM` — or `null` when it has none.
 *
 * ── AN OCCURRENCE IS A CIVIL DAY, AN OPTIONAL WALL TIME, AND A ZONE ──────────────────────────
 * The `moment` object is a closed value with three kinds (ADR 2026-09-08):
 *
 *   `Day`    a civil date and the zone it was cut in, NO hour. A remote plan day. Its `startUtc` is
 *            local midnight in that zone, kept so the server can compare days in SQL — a query
 *            artefact that prints `0:00`, or `22:00` the previous evening for a reader west of it.
 *   `Booked` a civil date, a chosen wall time, and the zone it was chosen in. `startUtc` is that
 *            wall time converted: the true moment, rendered on the reader's own clock.
 *   `Lived`  the instant the client tapped start. `time` is null and the occurrence still HAS an
 *            hour — the instant itself. This is the 2026-09-08 defect: a session started at 18:53
 *            in Warsaw read 16:53 on the trainer's calendar, because a real instant was read as a
 *            wall clock and converted a second time. A reader that tested `time` for the hour would
 *            hide the clock instead, which is the other half of the trap.
 *
 * ── WHAT THIS RETIRED ────────────────────────────────────────────────────────────────────────
 * `readWallClockTime`, which sliced the digits off the front of `eventDate`/`scheduledDate` and
 * constructed no `Date` at all, on the doctrine that a booked hour is a wall clock about a place.
 * The doctrine was right and the FIELD was wrong: that one column carried three meanings, and a
 * self-paced session's real instant was the third. The wall clock now travels as a wall clock
 * (`moment.time`, with its zone) and the instant as an instant, so the reader can be honest about
 * both. `eventDate` is a deprecated projection and is read as a TIME nowhere in this app.
 *
 * Midnight is a real answer now, not the absence of one: `Day` says "no hour" by its kind, so a
 * 00:00 booking — representable since the ADR — prints its hour like any other.
 *
 * ── THE FALLBACK ARM ─────────────────────────────────────────────────────────────────────────
 * A response with no `moment`, or naming a kind this build has never heard of, falls back to
 * `eventInstant` — the legacy true moment, `null` for an occurrence that names no hour. It is
 * defence against a rolled-back backend, not a state the current one produces, and it goes when the
 * legacy fields do. `eventDate` is deliberately NOT a second fallback: reading it as a clock is the
 * retired doctrine, and it is what put 16:53 on the calendar.
 */
export function occurrenceClock(occurrence: OccurrenceTime): string | null {
  const at = occurrenceInstant(occurrence);
  if (at === null) return null;

  const read = new Date(at);
  return `${String(read.getHours())}:${String(read.getMinutes()).padStart(2, '0')}`;
}

/**
 * The instant this occurrence HAPPENS at, in epoch ms — or `null` when it names no hour.
 *
 * The same decision {@link occurrenceClock} prints, before it is worded, because two questions are
 * asked of it: what hour does this read at, and WHICH DAY does it land on. A reader that answered
 * the first from the instant and the second from a stamped date put a block reading `3:45` in the
 * previous day's square for everyone east of the zone it was booked in.
 *
 * `null` is the load-bearing half of the return type: an occurrence with no hour is not an
 * occurrence at midnight, and the caller must place it by the civil day it NAMES instead.
 */
export function occurrenceInstant(occurrence: OccurrenceTime): number | null {
  const { moment } = occurrence;
  if (moment?.kind === DAY_KIND) return null;

  const instant =
    moment && KINDS_WITH_AN_HOUR.includes(moment.kind ?? '')
      ? moment.startUtc
      : occurrence.eventInstant;
  if (instant === null || instant === undefined) return null;

  const at = new Date(instant).getTime();
  return Number.isNaN(at) ? null : at;
}

/**
 * Local midnight of the civil date `YYYY-MM-DD` names, in epoch ms — or `null` when it is not one.
 *
 * Read off the DIGITS and handed to the local-time constructor. `new Date("2026-07-02")` parses as
 * UTC midnight and renders as 1 July in every western zone, which is the whole reason a civil date
 * never goes through an instant: `moment.date` is the day the occurrence was cut in ITS zone, and
 * re-reading it in the reader's would move a remote plan day off the square it belongs to.
 */
export function civilDayInstant(date: string): number | null {
  const parsed = parseIsoDate(date.slice(0, 10));
  return parsed === null ? null : parsed.getTime();
}

/** A wire instant as epoch milliseconds, or `null` when it is absent or unparseable. */
export function parseInstant(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined) return null;
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : null;
}

/** Local midnight of `date`, as a new `Date`. */
export function atMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * `days` after `base`, at local midnight.
 *
 * Date arithmetic through `setDate`, not `+ n * 86_400_000`: a spring-forward day is 23 hours long,
 * so millisecond arithmetic lands on 23:00 of the previous day and `toIsoDate` then reports the
 * wrong date for the whole rest of the plan. `setDate` rolls the calendar, which is what a "one day
 * later" means to a trainer.
 */
export function addDays(base: Date, days: number): Date {
  const next = atMidnight(base);
  next.setDate(next.getDate() + days);
  return next;
}

/**
 * The Monday of the calendar week `date` falls in, at local midnight. Monday itself answers itself.
 *
 * Monday-anchored because every week this app draws is — the training-week strip, the plan
 * calendar, and now the assignment projection, whose `T1` row is the week the start date lands in
 * rather than the seven days following it. `isoDayOfWeek` is the one place the 1..7 convention is
 * stated, so the arithmetic borrows it rather than repeating `getDay() === 0 ? 7 : getDay()` for
 * the third time in this module.
 */
export function mondayOf(date: Date): Date {
  return addDays(date, 1 - isoDayOfWeek(date));
}

/**
 * A calendar date as the reader's own locale writes it, with the year — "3 sie 2026".
 *
 * The year is not optional here. Every caller is naming a BOUND or a moved session, and a plan may
 * legally start up to two years out, so "3 sie" alone would be ambiguous in precisely the cases the
 * copy exists for.
 */
export function formatCalendarDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * A `YYYY-MM-DD` civil date written the way the reader writes one — „31.08.2026" in Polish. A
 * civil date carries no zone, so it is formatted in UTC; anything that is not one is handed back
 * as it came. The survey editor's date field and a filled survey's date answer both print through
 * it, so the two cannot write one date two ways.
 */
export function writtenCivilDate(value: string, locale: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

/** A day, in milliseconds. Whole-day arithmetic over UTC midnights never crosses a DST boundary. */
export const MS_PER_DAY = 86_400_000;

/**
 * Whole calendar days since a timestamp, or `null` when it is absent or unparseable.
 *
 * Calendar days, not 24-hour blocks: a workout finished at 23:00 yesterday is "wczoraj" at 08:00
 * today. Both instants are reduced to a local midnight before subtracting, so a DST transition
 * cannot turn "yesterday" into "2 days ago". Every caller that words an age reads this one clock —
 * the roster's recency and churn line, the archive's request age, the home's activity rows and a
 * clip comment's day — so no two surfaces can say a different number about the same afternoon.
 *
 * A `Date` is accepted beside the wire's string because some callers parse at the API seam, and
 * round-tripping one back through `toISOString()` to be re-parsed here would be ceremony.
 *
 * A future timestamp reports `0` rather than a negative count: an instant can land a few seconds
 * ahead of the browser through ordinary clock skew.
 */
export function calendarDaysSince(at: string | Date | null, now: Date): number | null {
  if (at === null) return null;

  const then = typeof at === 'string' ? new Date(at) : at;
  if (Number.isNaN(then.getTime())) return null;

  const localMidnight = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.max(0, Math.round((localMidnight(now) - localMidnight(then)) / MS_PER_DAY));
}

/**
 * A `DateOnly` string as a UTC midnight. `NaN` for anything that is not one.
 *
 * Promoted out of `pages/client-detail/model/trendsRange` on 17 September 2026, when
 * `features/survey-report` became its second consumer: the survey history counts cadence steps
 * between two cycle dates, and a feature may not reach into a page. One spelling of "a wall-clock
 * date as an instant", so the two cannot disagree about what a malformed date is.
 */
export function parseDateOnly(date: string): number {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T00:00:00Z`) : Number.NaN;
}
